"use client";

import { useEffect, useMemo, type MouseEvent, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  CheckCircle2,
  CircleDot,
  Flame,
  List,
  Menu,
  MessageSquare,
  PenLine,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  MobileBottomToolRegistration,
  useMobileBottomTools,
  type MobileBottomToolItem,
} from "@/components/mobile-bottom-tools";

/** 서버 페이지는 아이콘 컴포넌트(함수)를 client 로 넘길 수 없으므로 문자열 키로 고른다. */
const DOCK_ICONS = {
  list: List,
  flame: Flame,
  user: UserRound,
  pen: PenLine,
  menu: Menu,
  comment: MessageSquare,
  open: CircleDot,
  closed: CheckCircle2,
} satisfies Record<string, LucideIcon>;

export type CommunityDockIcon = keyof typeof DOCK_ICONS;

interface DockToolBase {
  id: string;
  /** dock 버튼 라벨(2~4음절). */
  label: string;
  /** WCAG 2.5.3: 눈에 보이는 `label` 을 부분 문자열로 포함해야 한다. */
  ariaLabel?: string;
  icon: CommunityDockIcon;
  /** 미지정 시 배열 순서. 다른 컴포넌트(예: 추천 버튼)가 사이에 끼어들 때만 지정한다. */
  order?: number;
}

/** 서버 페이지가 직렬화 가능한 형태로 넘기는 dock 도구 설명자. */
export type CommunityDockTool =
  | (DockToolBase & { kind: "view"; href: string; selected: boolean })
  | (DockToolBase & { kind: "link"; href: string })
  | (DockToolBase & { kind: "scroll"; targetId: string })
  | (DockToolBase & {
      kind: "sheet";
      title: string;
      description?: string;
      content: ReactNode;
    });

/** 앵커 위 고정 Navbar 만큼 띄워 스크롤한다(auto-hide Navbar 가 숨은 상태면 살짝 여유가 남는 정도). */
function scrollToElement(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const navbarHeight = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--mobile-navbar-height"),
  );
  const offset = (Number.isFinite(navbarHeight) ? navbarHeight : 64) + 8;
  const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
  const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
  window.scrollTo({ top, behavior });
}

/**
 * 커뮤니티 게시판(질문·버그 `/community/issues`, 감자단 잡담 `/community/talk`, JCG-3029)
 * 모바일 하단 dock 등록기.
 *
 * 데스크탑 셸(≥1024px)에서는 좌/우 사이드바가 같은 역할을 하고 dock provider 도 없으므로 아무것도
 * 그리지 않는다. 클래식 레이아웃(<1024px)에서는 `LayoutSwitch` 가 둔 공통 `MobileBottomToolProvider` 에
 * 도구를 등록해 `/my`·글 상세와 같은 하단 dock 으로 목록 전환·글쓰기·댓글 이동·더보기 시트를 제공한다.
 *
 * 서버 페이지가 현재 필터·링크·시트 내용(서버 렌더 카드)을 설명자로 넘기고, 여기서 router/시트 닫기와
 * 아이콘을 붙인다. 시트 안 링크를 누르면 시트를 닫는다(같은 세그먼트 재렌더에서는 등록 컴포넌트가
 * 살아 있어 자동으로 닫히지 않기 때문).
 */
export default function CommunityBoardMobileDock({ tools }: { tools: CommunityDockTool[] }) {
  const { inShell } = useArticleShellSlots();
  if (inShell) return null;
  return <DockRegistrations tools={tools} />;
}

function DockRegistrations({ tools }: { tools: CommunityDockTool[] }) {
  const router = useRouter();
  const pathname = usePathname();
  const { closeSheet } = useMobileBottomTools();

  // 경로가 바뀌면(목록 ↔ 상세 등) 이전 페이지에서 열어 둔 시트를 닫는다.
  useEffect(() => {
    closeSheet();
  }, [closeSheet, pathname]);

  const items = useMemo<MobileBottomToolItem[]>(
    () =>
      tools.map((tool, index) => {
        const base = {
          id: tool.id,
          label: tool.label,
          ariaLabel: tool.ariaLabel,
          icon: DOCK_ICONS[tool.icon],
          order: tool.order ?? index,
        };
        switch (tool.kind) {
          case "view":
            return {
              ...base,
              kind: "view",
              selected: tool.selected,
              onSelect: () => router.push(tool.href),
            };
          case "link":
            return { ...base, kind: "action", onAction: () => router.push(tool.href) };
          case "scroll":
            return { ...base, kind: "action", onAction: () => scrollToElement(tool.targetId) };
          case "sheet": {
            const closeOnLinkClick = (event: MouseEvent<HTMLDivElement>) => {
              if ((event.target as HTMLElement | null)?.closest("a")) closeSheet();
            };
            return {
              ...base,
              kind: "panel",
              sheet: {
                title: tool.title,
                description: tool.description,
                // 카드 몇 장 분량의 자연 높이라 콘텐츠만큼만 차지(넘치면 시트 안 스크롤). 목차·필기처럼 dock 에 붙인다.
                resizable: false,
                attached: true,
                content: <div onClickCapture={closeOnLinkClick}>{tool.content}</div>,
              },
            };
          }
        }
      }),
    [closeSheet, router, tools],
  );

  return (
    <>
      {items.map((item) => (
        <MobileBottomToolRegistration key={item.id} tool={item} />
      ))}
    </>
  );
}
